import { useMemo, useRef } from 'react'
import { format } from 'date-fns'

import { FindNextUnavailableDay } from './dayAvailability'

export default function useFirstUnavailableDate(
  startDate: Date | null,
  findNextUnavailableDay: FindNextUnavailableDay,
) {
  // Memoize firstUnavailable using a ref + memo, so that calculations of
  // the new value can be derived off of the previous value.
  const firstUnavailable = useRef<{
    startDate: Date | null
    unavailableDate: Date | null
  }>({ startDate: null, unavailableDate: null })

  // Because `current` is set on every render, we can rely upon it being up
  // to date for that specific render pass (normally if a ref is async it may
  // not trigger a re-render but that isn't a concern here):
  firstUnavailable.current = useMemo(() => {
    const { current } = firstUnavailable
    if (startDate) {
      // If a start date is set we want to calculate the next unavailable date,
      // but only if we haven't found one already (this avoids problems if the
      // user pages so far forward in the calendar that the data set no longer
      // includes the start date, such that recalculating the next unavailable
      // date would be inaccurate):
      if (!current.unavailableDate || startDate !== current.startDate) {
        return {
          startDate,
          unavailableDate: findNextUnavailableDay(
            format(startDate, 'yyyy-MM-dd'),
          ),
        }
      }

      return current
    }

    // Clear out when there is no longer a start date selected:
    return { startDate: null, unavailableDate: null }
  }, [startDate, findNextUnavailableDay])

  return firstUnavailable.current.unavailableDate
}
